import React, { useMemo, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import PreconditionDefinitionsTable from './components/PreconditionDefinitionsTable';
import PreconditionDefinitionForm from './components/PreconditionDefinitionForm';
import usePreconditionDefinitionsData from './hooks/usePreconditionDefinitionsData';
import GenericTableFilter from '../../../components/common/GenericTableFilter';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useLmsPermission } from '../hooks/useLmsPermission';
import { LMS } from '../../../constants/permissionCodes';

/**
 * Ön Koşul Tanımları yönetim ekranı. Bkz. docs/LMS-Egitim-Katalogu-ve-Egitmenler-API 2.md §6.
 */
const PreconditionDefinitionsPage = () => {
    const { th } = useLanguage();
    const { can } = useLmsPermission();
    const [selectedItem, setSelectedItem] = useState(null);
    const [dialogVisible, setDialogVisible] = useState(false);
    const toast = useRef(null);

    const { preconditionApi, isLoading, lazyParams, onPage, saveDefinition, activeFilters, handleFilterChange, initFilters, deleteDefinition, refetch } = usePreconditionDefinitionsData();

    const handleEdit = (rowData) => {
        setSelectedItem(rowData);
        setDialogVisible(true);
    };

    const handleCreateClick = () => {
        setSelectedItem(null);
        setDialogVisible(true);
    };

    const handleHide = () => {
        setDialogVisible(false);
        setSelectedItem(null);
    };

    const handleSubmit = async (formData) => {
        const success = await saveDefinition(formData);
        if (success) {
            handleHide();
            refetch?.();
        }
    };

    const handleDelete = (id) => {
        if (!id) return;
        confirmDialog({
            message: th('preconditionDefinitions.confirmDeleteMessage'),
            header: th('common.delete'),
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    await deleteDefinition(id);
                    toast.current?.show({ severity: 'success', summary: th('common.success'), detail: th('preconditionDefinitions.deleteSuccess'), life: 3000 });
                    handleHide();
                    refetch?.();
                } catch {
                    toast.current?.show({ severity: 'error', summary: th('common.error'), detail: th('preconditionDefinitions.deleteError'), life: 4000 });
                }
            }
        });
    };

    const filterFields = useMemo(
        () => [
            { field: 'code', headerName: th('preconditionDefinitions.filterCode'), type: 'text' },
            { field: 'text', headerName: th('preconditionDefinitions.filterText'), type: 'text' }
        ],
        [th]
    );

    const onApplyFilters = (uiFilters) => {
        handleFilterChange(uiFilters);
    };

    const onResetFilters = () => {
        initFilters();
    };

    return (
        <div className="card">
            <Toast ref={toast} />
            <ConfirmDialog />

            <div className="flex justify-content-between align-items-center mb-4">
                <h1 className="m-0">{th('preconditionDefinitions.title')}</h1>
                {can(LMS.DEFINITION_CREATE) && <Button label={th('preconditionDefinitions.newButton')} icon="pi pi-plus" onClick={handleCreateClick} />}
            </div>

            <div className="mb-2">
                <GenericTableFilter filterFields={filterFields} initialFilters={{ code: activeFilters?.code ?? '', text: activeFilters?.text ?? '' }} onSearch={() => {}} onApplyFilters={onApplyFilters} onResetFilters={onResetFilters} />
            </div>

            <PreconditionDefinitionsTable data={preconditionApi} totalCount={preconditionApi?.totalCount} loading={isLoading} lazyParams={lazyParams} onPage={onPage} onEdit={handleEdit} />

            {dialogVisible && (
                <PreconditionDefinitionForm
                    visible={dialogVisible}
                    onHide={handleHide}
                    onSubmit={handleSubmit}
                    initialData={selectedItem}
                    loading={isLoading}
                    handleDelete={can(LMS.DEFINITION_DELETE) ? handleDelete : undefined}
                    readOnly={!!selectedItem && !can(LMS.DEFINITION_UPDATE)}
                />
            )}
        </div>
    );
};

export default PreconditionDefinitionsPage;
